import { Injectable, ViewContainerRef } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { EntryPointService, HtmlDataURI, HtmlExportService, MagnetService } from '@mirage-projects/cw-commons';
import {
    PcvizCollageWebFileBrowserListResult,
    PcvizDBUnitController,
    ReportVolume,
    VolumeReportEntries,
    VolumeReportFormatted,
} from '@mirage-projects/cw-models';
import { DataAccessPcvizService, DbPcvizService, UtilsPcvizService } from '@mirage-projects/cw-pcviz-core';

import { VolumeReportFormatService } from './volume-report-format.service';
import { VolumeReportComponent } from './volume-report.component';

export type VolumeHtmlDownloadParams = {
    projectName: string;
    layerId: string;
    volumeScreenshot?: PcvizCollageWebFileBrowserListResult;
    pointCloudScreenshot?: PcvizCollageWebFileBrowserListResult;
    isDownload?: boolean;
};

/**
 * Service that loads and creates the volume data to export
 */
@Injectable({ providedIn: 'root' })
export class VolumeReportService {
    private readonly reportFileName = 'report.json';

    constructor(
        private dataAccessPcvizService: DataAccessPcvizService,
        private utilsPcvizService: UtilsPcvizService,
        private dbPcvizService: DbPcvizService,
        private magnetService: MagnetService,
        private volumeReportFormatService: VolumeReportFormatService,
        private htmlExportService: HtmlExportService,
        private translateService: TranslateService,
        private entryPointService: EntryPointService
    ) {}

    /**
     * Loads the volume data from the server and returns it in an object
     * @param layerId The layer id
     */
    async loadReportData(layerId: string): Promise<ReportVolume | undefined> {
        const fileBrowser = this.dataAccessPcvizService.createCollageWebFileBrowser();
        this.utilsPcvizService.configureDriver(fileBrowser, this.entryPointService.getEntryPointsForCore());

        const fileList = await fileBrowser.list({ id: layerId }, 'layers', `3d_models`);
        const reportFile = fileList.find((file) => file.name === this.reportFileName);

        if (!reportFile) {
            return;
        }

        const jsonData = await fileBrowser.retrieve(reportFile);

        if (!jsonData) {
            return;
        }

        const reportData = JSON.parse(jsonData);

        if (this.magnetService.isSite()) {
            delete reportData.existingSurface;
            delete reportData.designSurface;
        }

        return reportData;
    }

    /**
     * Formats the volume data to add the required number units
     * @param report Report data
     */
    addUnitsToReport(report: Partial<ReportVolume>): VolumeReportFormatted {
        const reportFormatted: VolumeReportFormatted = {};

        for (const [key, value] of Object.entries(report) as VolumeReportEntries) {
            switch (true) {
                case key.includes('Area'): {
                    reportFormatted[key] = this.toUserMeasureUnit(value as number, this.unitController.AREA_TYPE);
                    break;
                }
                case key.includes('Volume'): {
                    reportFormatted[key] = this.toUserMeasureUnit(value as number, this.unitController.VOLUME_TYPE);
                    break;
                }
                case key.includes('Distance'): {
                    reportFormatted[key] = this.toUserMeasureUnit(value as number, this.unitController.DISTANCE_TYPE);
                    break;
                }
                default:
                    reportFormatted[key] =
                        key !== 'originPointCoordinates' ? `${value}` : this.getFormattedOriginPoints(report.originPointCoordinates);
                    break;
            }
        }

        return reportFormatted;
    }

    /**
     * Downloads the generated volume data string to the client machine
     * @param reportText String with the formatted data
     */
    downloadTextReport(reportFormatted: VolumeReportFormatted, projectName: string): void {
        const mimeType = 'text/plain';
        const txtFile = this.volumeReportFormatService.formatFile(reportFormatted, projectName);
        const blob = new Blob([txtFile], { type: mimeType });
        const dateSuffix = this.getDateSufix();
        const fileName = this.magnetService.isSite() ? `VolumeReport_${dateSuffix}.txt` : 'volume_report.txt';
        this.runDownload(blob, fileName, mimeType);
    }

    downloadCsvReport(reportFormatted: VolumeReportFormatted, projectName: string): void {
        const utf8bom = new Uint8Array([0xef, 0xbb, 0xbf]);
        const mimeType = 'text/plain;charset=utf-8';
        const csvFile = this.volumeReportFormatService.formatFileAsCsv(reportFormatted, projectName);
        const blob = new Blob([utf8bom, csvFile], { type: mimeType });
        const dateSuffix = this.getDateSufix();
        const fileName = `VolumeReport_${dateSuffix}.csv`;
        this.runDownload(blob, fileName, mimeType);
    }

    async downloadHtmlReport(inputs: VolumeHtmlDownloadParams, viewContainerRef: ViewContainerRef): Promise<void> {
        const dataUri = await this.htmlExportService.getReportHtmlDataUrl<VolumeReportComponent>(
            VolumeReportComponent,
            viewContainerRef,
            inputs,
            undefined,
            async (componentRef) => {
                await firstValueFrom(componentRef.instance.initComplete$);
                componentRef.changeDetectorRef.detectChanges();
            }
        );
        const mimeType = 'text/html;charset=utf-8';
        const dateSuffix = this.getDateSufix();
        const fileName = `VolumeReport_${dateSuffix}.html`;
        this.runDownload(dataUri, fileName, mimeType);
    }

    /**
     * Translates calculationMethod and designPlaneSettings string properties for the a given VolumeReportFormatted object
     * @param reportFormatted formatted report data object
     */
    translateReportStringValues(reportFormatted: VolumeReportFormatted): VolumeReportFormatted {
        if (reportFormatted.calculationMethod) {
            const translationKey = 'SCANPROC.CALC_' + reportFormatted.calculationMethod.toUpperCase();
            reportFormatted.calculationMethod = this.translateService.instant(translationKey);
        }

        if (reportFormatted.designPlaneSettings) {
            const translationKey = 'SCANPROC.' + reportFormatted.designPlaneSettings.toUpperCase();
            reportFormatted.designPlaneSettings = this.translateService.instant(translationKey);
        }
        return reportFormatted;
    }

    /**
     * Converts the origin points array to a formatted string
     * @param pointCoordinates Coordinates array
     */
    private getFormattedOriginPoints(pointCoordinates: number[] = []): string {
        return pointCoordinates.join(' ');
    }

    private runDownload(data: Blob | HtmlDataURI, filename: string, mimeType: string): void {
        const a = document.createElement('a');
        a.download = filename;
        a.href = data instanceof Blob ? URL.createObjectURL(data) : data;
        a.dataset['downloadurl'] = [mimeType, a.download, a.href].join(':');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(function () {
            URL.revokeObjectURL(a.href);
        }, 1500);
    }

    private getDateSufix(): string {
        return new Date().toISOString().substring(0, 10).split('-').join('');
    }

    private toUserMeasureUnit(measureValue: number, measureUnit: string): string {
        const unitsControllerInstance = this.dbPcvizService.getInstanceDB().getController('Units');

        return unitsControllerInstance.convertToUserUnit(measureValue, measureUnit);
    }

    private get unitController(): PcvizDBUnitController {
        return this.dbPcvizService.getUnitController();
    }
}
